import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    ADDON_TO_PRICING_FEATURE_KEY,
    checkIsAICreditPricing,
    checkIsFreeProductKey,
    METER_PRICES,
} from '@gitbook/app-pricing';
import { confirm, DialogRoot, toast, useDialogTriggerState } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiClient, apiQuery, useGetUpgradeAction } from '../../state';
import { EnableBillingUsageAddonDialog } from './EnableBillingUsageAddonDialog';

type UseBillingUsageAddonController = {
    isEnabled: boolean;

    /**
     * Enable the addon for the organization.
     */
    enable: () => Promise<void>;

    /**
     * Disable the addon for the organization.
     */
    disable: () => Promise<void>;

    /**
     * Wrap a function to ensure the addon is enabled.
     */
    withAddon: (onConfirm: () => Promise<void>) => Promise<void>;
};

/**
 * Hook to ensure an addon is enabled
 */
export function useBillingUsageAddon(props: {
    organizationId: string;
    meterName: api.BillingMeter;
    site?: api.Site;
}): [React.ReactNode, UseBillingUsageAddonController] {
    const { organizationId, meterName, site } = props;

    const resolveEnableRef = React.useRef<null | ((confirmed: boolean) => void)>(null);
    const resolveDialog = (confirmed: boolean) => {
        resolveEnableRef.current?.(confirmed);
        resolveEnableRef.current = null;
    };

    const enableDialogTrigger = useDialogTriggerState({
        onOpenChange: (open) => {
            if (!open) {
                resolveDialog(false);
            }
        },
    });

    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', {
            organizationId,
        })
    );
    const upgrade = useGetUpgradeAction({
        organization,
        featureKey: ADDON_TO_PRICING_FEATURE_KEY[meterName],
    });

    const isEnabled =
        !checkIsFreeProductKey(organization.plan) &&
        (organization.billing?.usageAddons?.[meterName]?.enabled ?? false);

    const enable = useEventCallback(async (args?: { onConfirm?: () => Promise<void> }) => {
        if (!organization.ai) {
            toast.error('GitBook AI is disabled for this organization.', null);
            return;
        }

        analytics.logEvent({
            type: 'billing_start_enable_usage_addon',
            billingMeter: meterName,
            billingInterval: organization.billing?.interval ?? api.BillingInterval.Monthly,
            organization: organizationId,
        });

        const confirmed = await new Promise<boolean>((resolve) => {
            resolveEnableRef.current = resolve;
            enableDialogTrigger.open();
        });

        if (confirmed) {
            await args?.onConfirm?.();
        }
    });

    const disable = useEventCallback(async () => {
        const meterPrice = METER_PRICES[meterName];
        await confirm({
            kind: 'danger',
            title: `Disable ${meterPrice.addon?.title ? `${meterPrice.addon?.title} addon` : 'addon'}`,
            confirmLabel: 'Confirm and disable',
            description: (
                <p>
                    Are you sure you want to disable the{' '}
                    {meterPrice.addon?.title ? `${meterPrice.addon?.title} addon` : 'addon'}?
                </p>
            ),
            onConfirm: async () => {
                await toast.catch(async () => {
                    await apiClient.billing.disableOrganizationBillingMeterAddon(
                        organizationId,
                        meterName
                    );
                }, 'Error while disabling usage addon');
            },
        });
    });

    /**
     * Wrap a function to ensure the addon is enabled.
     */
    const withAddon = useEventCallback((onConfirm: () => Promise<void>) => {
        if (!organization.ai) {
            toast.error('GitBook AI is disabled for this organization.', null);
            return Promise.resolve();
        }

        const isAIUsagePricing = checkIsAICreditPricing(organization.billing?.creditsBundle);
        if (isAIUsagePricing) {
            if (upgrade) {
                return upgrade.onPress();
            }

            return onConfirm();
        }

        if (!isEnabled) {
            return enable({ onConfirm });
        }

        return onConfirm();
    });

    return [
        <DialogRoot key="enable-billing-usage-addon-dialog" {...enableDialogTrigger.rootProps}>
            <EnableBillingUsageAddonDialog
                organization={organization}
                meterName={meterName}
                site={site}
                onConfirm={() => {
                    resolveDialog(true);
                }}
            />
        </DialogRoot>,
        {
            isEnabled,
            enable,
            disable,
            withAddon,
        },
    ];
}
